import { useState } from 'react';
import cloneDeep from 'lodash.clonedeep';
import { useFormContext } from 'react-hook-form';
import LocalizeItemModal from '@features/LocalizeItemModal';
import Button from '@components/Button';
import { Typography } from '@components/Typography';
import DragAndDrop, { SortableItem } from '@components/DragAndDrop';
import { useTranslation } from '@hooks';
import { PREVIEW_LOCALES, LANGUAGES_MAP } from '@constants/microsites';
import QuestionItem from './QuestionItem';

const Questions = ({ isView, account }) => {
    const [localizeModal, setLocalizeModal] = useState({ open: false, index: null, ctx: null });

    const { t } = useTranslation();
    const { watch, setValue } = useFormContext();

    const questions = watch('questions');

    const availableLanguages = LANGUAGES_MAP[account.locale];
    const currentLocale = PREVIEW_LOCALES[account.locale];

    const onAddQuestion = () => {
        const questionsCopy = cloneDeep(questions);
        const newQuestions = {
            ...questionsCopy,
            [Object.keys(questionsCopy).length]: [
                {
                    title: t('common.title'),
                    question: t('common.question'),
                    language: availableLanguages[0],
                },
            ],
        };
        setValue('questions', newQuestions, { shouldDirty: true });
    };

    const onClickQuestion = key => {
        const questionsCopy = cloneDeep(questions);
        questionsCopy[key][0].isOpen = !questionsCopy[key][0].isOpen;
        setValue('questions', questionsCopy, { shouldDirty: true });
    };

    const onChangeQuestion = (e, key, lang) => {
        const questionsCopy = cloneDeep(questions);
        // If changing title, update it for all languages in this question group
        if (e.target.name === 'title') {
            questionsCopy[key] = questionsCopy[key].map(q => ({
                ...q,
                title: e.target.value,
            }));
        } else {
            // INFO: find index of the link to change
            const questionIndex = questionsCopy[key].findIndex(l => l.language === lang);
            // INFO: update it with necessary field
            questionsCopy[key][questionIndex] = {
                ...questionsCopy[key][questionIndex],
                [e.target.name]: e.target.value,
            };
        }
        setValue('questions', questionsCopy, { shouldDirty: true });
    };

    const onDeleteQuestion = key => {
        const questionsCopy = cloneDeep(questions);
        delete questionsCopy[key];
        const copyUpdatedIndexes = Object.values(questionsCopy).reduce(
            (prev, value, index) => ({ ...prev, [index]: value }),
            {},
        );
        setValue('questions', copyUpdatedIndexes, { shouldDirty: true });
    };

    const dndEntries = Object.entries(questions);

    return (
        <>
            <div
                style={{
                    margin: '16px 0',
                    display: 'flex',
                    gap: '16px',
                    justifyContent: 'space-between',
                    width: '100%',
                }}
            >
                <div>
                    <Typography variant="body2">{t('FeedbackSurveys.surveyQuestions')}</Typography>
                    <Typography
                        variant="caption"
                        sx={{ color: 'rgba(0, 0, 0, 0.6)', display: 'block', margin: '4px 0 8px' }}
                    >
                        {t('FeedbackSurveys.questionsDescription')}
                    </Typography>
                </div>
                <Button
                    variant="outlined"
                    onClick={onAddQuestion}
                    disabled={isView}
                    sx={{ whiteSpace: 'nowrap', minWidth: 'fit-content' }}
                >
                    {t('FeedbackSurveys.addQuestion')}
                </Button>
            </div>
            <div style={{ marginTop: 8, marginBottom: 24 }}>
                <DragAndDrop
                    items={dndEntries.map(i => i[0])}
                    data={questions}
                    onDragEndCb={result => {
                        setValue('questions', result, { shouldDirty: true });
                    }}
                >
                    {dndEntries.map(([key, questions]) => {
                        const question = questions.find(l => l.language === currentLocale) || questions[0];
                        return (
                            <SortableItem key={key} id={key}>
                                <QuestionItem
                                    index={key}
                                    isView={isView}
                                    question={question}
                                    isOpen={questions[0]?.isOpen}
                                    onClick={onClickQuestion}
                                    onChange={onChangeQuestion}
                                    onDelete={onDeleteQuestion}
                                    setLocalizeModal={setLocalizeModal}
                                />
                            </SortableItem>
                        );
                    })}
                </DragAndDrop>
            </div>
            {localizeModal.open ? (
                <LocalizeItemModal
                    modal={localizeModal}
                    onClose={() => setLocalizeModal({ open: false, index: null, ctx: null })}
                    currentLocale={currentLocale}
                    entity="questions"
                    propName="question"
                    withChangeTitle={false}
                    withChangeEntity
                />
            ) : null}
        </>
    );
};

export default Questions;
